import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, XCircle } from "lucide-react";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";

interface ExportColumn {
  id: string;
  label: string;
  dbColumn?: string;
  defaultSelected: boolean;
}

const exportColumns: ExportColumn[] = [
  { id: "store", label: "Loja (CAVNUM_LOJA)", dbColumn: "CAVNUM_LOJA", defaultSelected: true },
  { id: "docCode", label: "Documento (CAVDOCCOD)", dbColumn: "CAVDOCCOD", defaultSelected: true },
  { id: "register", label: "Caixa (CAVNUM_CAIXA)", dbColumn: "CAVNUM_CAIXA", defaultSelected: false },
  { id: "nsu", label: "NSU (CAVNSU_ORIGEM)", dbColumn: "CAVNSU_ORIGEM", defaultSelected: false },
  { id: "value", label: "Valor (CAVVALOR_TRANSACAO)", dbColumn: "CAVVALOR_TRANSACAO", defaultSelected: true },
  { id: "terminalDate", label: "Data Terminal", dbColumn: "CAVDATA_HORA_TERMINAL", defaultSelected: false },
  { id: "status", label: "Status", defaultSelected: true },
  { id: "processingResult", label: "Mensagem de Erro", dbColumn: "CAVRESULTADO_PROCESSAMENTO", defaultSelected: false },
  { id: "processingDate", label: "Data Processamento", dbColumn: "CAVDATA_PROCESSAMENTO", defaultSelected: false },
  { id: "requestingTerminal", label: "Terminal", dbColumn: "CAVTERMINAL_REQUISITANTE", defaultSelected: false },
  { id: "planId", label: "ID Plano (CAVNUMERO_PARCELAS)", dbColumn: "CAVNUMERO_PARCELAS", defaultSelected: false },
  { id: "planDescription", label: "Descricao Plano (TAX1NOM)", dbColumn: "TAX1NOM", defaultSelected: false },
  { id: "softDescriptor", label: "Soft Descriptor (CAVSOFT_DESCRIPTOR)", dbColumn: "CAVSOFT_DESCRIPTOR", defaultSelected: false },
  { id: "networkOrigin", label: "Rede Origem PDV (CAVREDE_ORIGEM_OPERACAO_PDV)", dbColumn: "CAVREDE_ORIGEM_OPERACAO_PDV", defaultSelected: false },
  { id: "adminInstallment", label: "Parcelado Admin (CAVPARCELADO_ADMIN)", dbColumn: "CAVPARCELADO_ADMIN", defaultSelected: false },
  { id: "cardholderId", label: "ID Portador (CAVID_PORTADOR)", dbColumn: "CAVID_PORTADOR", defaultSelected: false },
  { id: "registerStatus", label: "Status Registro (CAVSTATUS_REG)", dbColumn: "CAVSTATUS_REG", defaultSelected: false },
  { id: "createdAt", label: "Criado Em (CREATED_AT)", dbColumn: "CREATED_AT", defaultSelected: false },
  { id: "lastUpdated", label: "Atualizado Em (LAST_UPDATED)", dbColumn: "LAST_UPDATED", defaultSelected: false },
];

const getDefaultSelectedColumns = () =>
  new Set(exportColumns.filter((c) => c.defaultSelected).map((c) => c.id));

interface ExportModalContentProps {
  selectedCount: number;
  selectAllFiltered: boolean;
  totalFiltered: number;
  onExport: (columns: string[]) => void;
}

export function ExportModalContent({
  selectedCount,
  selectAllFiltered,
  totalFiltered,
  onExport,
}: ExportModalContentProps) {
  const { handleModal } = useDialogModal();
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(
    getDefaultSelectedColumns
  );

  const rowCount = selectAllFiltered ? totalFiltered : selectedCount;
  const showWarning = rowCount > 10000 && rowCount <= 50000;
  const showError = rowCount > 50000;

  const toggleColumn = (columnId: string) => {
    setSelectedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnId)) {
        next.delete(columnId);
      } else {
        next.add(columnId);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedColumns(new Set(exportColumns.map((c) => c.id)));
  };

  const clearAll = () => {
    setSelectedColumns(new Set());
  };

  const handleClose = () => {
    handleModal({ isOpen: false, element: null, title: "" });
  };

  const handleExport = () => {
    onExport(Array.from(selectedColumns));
    handleClose();
  };

  const canExport = selectedColumns.size > 0 && !showError;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Exportando: {rowCount.toLocaleString("pt-BR")} transaco{rowCount !== 1 ? "es" : ""} selecionada{rowCount !== 1 ? "s" : ""}
      </p>

      {showWarning && (
        <Alert className="bg-yellow-50 border-yellow-200">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            Voce esta exportando muitas transacoes. Isso pode demorar.
          </AlertDescription>
        </Alert>
      )}

      {showError && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            Limite maximo de 50.000 transacoes por exportacao.
            Por favor, aplique filtros para reduzir a quantidade.
          </AlertDescription>
        </Alert>
      )}

      <div>
        <p className="text-sm font-medium mb-2">Selecione as colunas:</p>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {exportColumns.map((column) => (
            <div key={column.id} className="flex items-center space-x-2">
              <Checkbox
                id={`export-col-${column.id}`}
                checked={selectedColumns.has(column.id)}
                onCheckedChange={() => toggleColumn(column.id)}
              />
              <label
                htmlFor={`export-col-${column.id}`}
                className="text-sm cursor-pointer"
              >
                {column.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={selectAll}>
          Selecionar Todos
        </Button>
        <Button variant="outline" size="sm" onClick={clearAll}>
          Limpar Selecao
        </Button>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleClose}>
          Cancelar
        </Button>
        <Button onClick={handleExport} disabled={!canExport}>
          Exportar CSV
        </Button>
      </div>
    </div>
  );
}

export { exportColumns };
